import {
  parsePhoneNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js";

/**
 * Formats a phone number according to the specified requirements:
 * - US and Canadian numbers (starting with +1): +1-xxx-xxx-xxxx format
 * - International numbers: Use libphonenumber-js formatting
 * @param phoneNumber The phone number to format
 * @returns Formatted phone number string or null if invalid
 */
export function formatPhoneNumber(
  phoneNumber: string | null | undefined,
): string | null {
  if (!phoneNumber) {
    return null;
  }

  // Clean the input
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Handle US/Canadian numbers (10 digits or 11 digits starting with 1)
  if (cleaned.length === 10) {
    return `+1-${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    const nationalNumber = cleaned.slice(1);
    return `+1-${nationalNumber.slice(0, 3)}-${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
  }

  // Handle international numbers with libphonenumber-js
  try {
    const parsed = parsePhoneNumber(phoneNumber);

    if (!parsed || !isValidPhoneNumber(phoneNumber)) {
      return null;
    }

    // For US and Canadian numbers (+1), use the specific format +1-xxx-xxx-xxxx
    if (parsed.countryCallingCode === "1") {
      const nationalNumber = parsed.nationalNumber;
      if (nationalNumber.length === 10) {
        return `+1-${nationalNumber.slice(0, 3)}-${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
      }
    }

    // For international numbers, use the standard libphonenumber-js formatting
    return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  } catch (error) {
    console.error("Error parsing phone number:", error);
    return null;
  }
}

/**
 * Validation result for phone number keystroke validation
 */
export type PhoneValidationResult =
  | { isValid: true; error: null }
  | {
      isValid: false;
      error:
        | "MUST_START_WITH_PLUS"
        | "INVALID_CHARACTERS"
        | "TOO_LONG"
        | "INVALID_LENGTH";
    };

/**
 * Validates a phone number during keystroke input (partial validation)
 * This allows users to type freely without errors for incomplete but valid numbers
 * @param phoneNumber The phone number to validate
 * @returns Validation result with error type if invalid
 */
export function validatePhoneNumberOnKeystroke(
  phoneNumber: string | null | undefined,
): PhoneValidationResult {
  // Empty or just "+" is valid (user is still typing)
  if (!phoneNumber || phoneNumber === "+") {
    return { isValid: true, error: null };
  }

  // Must start with +
  if (!phoneNumber.startsWith("+")) {
    return { isValid: false, error: "MUST_START_WITH_PLUS" };
  }

  // Check for invalid characters (only digits, spaces, dashes, parentheses allowed after +)
  const validCharsRegex = /^\+[\d\s\-()]+$/;
  if (!validCharsRegex.test(phoneNumber)) {
    return { isValid: false, error: "INVALID_CHARACTERS" };
  }

  // Use validatePhoneNumberLength for partial validation
  const lengthValidation = validatePhoneNumberLength(phoneNumber);

  // undefined = valid length (complete or incomplete but valid)
  // TOO_SHORT = fine for partial input (still typing)
  // Only error on TOO_LONG or INVALID_LENGTH
  if (lengthValidation === "TOO_LONG") {
    return { isValid: false, error: "TOO_LONG" };
  }

  if (lengthValidation === "INVALID_LENGTH") {
    return { isValid: false, error: "INVALID_LENGTH" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates a complete phone number (for form submission)
 * @param phoneNumber The phone number to validate
 * @returns true if valid and complete, false otherwise
 */
export function validateCompletePhoneNumber(
  phoneNumber: string | null | undefined,
): boolean {
  if (!phoneNumber) {
    return false;
  }

  try {
    const parsed = parsePhoneNumber(phoneNumber);
    return parsed.isValid();
  } catch {
    return false;
  }
}
